import { differenceInCalendarDays } from 'date-fns';
import type { Mode, Session } from '@timato/shared';
import { uuidv7 } from '@timato/shared';
import { db } from '@/db/db';
import { deleteRow, notDeleted, saveRow } from './repo';
import { isWork } from './stats';
import { recomputeTrackedSec } from './tasks';
import { teamIdForProject } from '@/team/cache';

export type SessionInput = { mode: Mode; startedAt: Date; endedAt: Date; goalText: string; projectId: string | null; taskId: string | null; labelIds: string[] };

const statusFor = (mode: Mode): Session['status'] => (mode === 'short' || mode === 'long' ? 'break' : 'completed');
const durationOf = (a: Date, b: Date) => Math.max(0, Math.round((b.getTime() - a.getTime()) / 1000));

/** A session typed in by hand (8c "+ Add session"). Carries the permanent edited marker from birth. */
export async function createManualSession(input: SessionInput): Promise<Session> {
  const s = await saveRow('sessions', {
    id: uuidv7(input.startedAt.getTime()), teamId: await teamIdForProject(input.projectId), taskId: input.taskId, projectId: input.projectId,
    mode: input.mode, status: statusFor(input.mode), startedAt: input.startedAt.toISOString(), endedAt: input.endedAt.toISOString(),
    durationSec: durationOf(input.startedAt, input.endedAt), goalText: input.goalText.trim(), labelIds: input.labelIds,
    source: 'manual', editedAt: new Date().toISOString(), editedBy: null, updatedAt: '', deletedAt: null,
  });
  await recomputeTrackedSec(s.taskId);
  return s;
}

/**
 * Edit an existing row. Status follows the mode; a skipped focus session becomes completed once its times are edited,
 * while a parked one stays parked (TW-32), still waiting to be resumed or finished. A row whose times are left alone
 * keeps its length: a run parked and resumed spans more than the time it holds, and the span would overstate it.
 */
export async function updateSession(s: Session, input: SessionInput): Promise<Session> {
  const startedAt = input.startedAt.toISOString(), endedAt = input.endedAt.toISOString();
  // The dialog works in whole minutes, so "left alone" is the same minutes as stored.
  const minute = (iso: string) => Math.floor(Date.parse(iso) / 60_000);
  const sameTimes = minute(startedAt) === minute(s.startedAt) && minute(endedAt) === minute(s.endedAt);
  const status = s.status === 'parked' && input.mode === 'track' ? 'parked' : statusFor(input.mode);
  const next = await saveRow('sessions', {
    ...s, teamId: await teamIdForProject(input.projectId), taskId: input.taskId, projectId: input.projectId, mode: input.mode, status,
    startedAt: sameTimes ? s.startedAt : startedAt, endedAt: sameTimes ? s.endedAt : endedAt, durationSec: sameTimes ? s.durationSec : durationOf(input.startedAt, input.endedAt),
    goalText: input.goalText.trim(), labelIds: input.labelIds, editedAt: new Date().toISOString(),
  });
  await Promise.all([recomputeTrackedSec(s.taskId), s.taskId !== next.taskId ? recomputeTrackedSec(next.taskId) : null]);
  return next;
}

export async function deleteSession(s: Session) {
  await deleteRow('sessions', s);
  await recomputeTrackedSec(s.taskId);
}
/** Finish a parked session from Sessions (TW-32): it is completed with the time it holds, no timer needed. */
export const finishParked = (s: Session) => saveRow('sessions', { ...s, status: 'completed' });

export async function restoreSession(s: Session) {
  await saveRow('sessions', { ...s, deletedAt: null });
  await recomputeTrackedSec(s.taskId);
}

/** Sessions whose interval intersects [start, end), excluding one id. Breaks overlap too - time is time. */
export async function findOverlaps(start: Date, end: Date, excludeId?: string): Promise<Session[]> {
  const from = new Date(start.getTime() - 24 * 3600_000).toISOString(); // sessions rarely exceed a day
  const candidates = await db.sessions.where('startedAt').between(from, end.toISOString(), true, false).toArray();
  return candidates.filter((s) => notDeleted(s) && s.id !== excludeId && Date.parse(s.endedAt) > start.getTime() && Date.parse(s.startedAt) < end.getTime());
}

/** Shrink `other` so it no longer intersects [start, end). Drops it if nothing would remain. */
export async function trimAround(other: Session, start: Date, end: Date) {
  const oStart = Date.parse(other.startedAt), oEnd = Date.parse(other.endedAt);
  let ns = oStart, ne = oEnd;
  if (oStart < start.getTime() && oEnd > start.getTime()) ne = start.getTime(); // other runs into ours: cut its tail
  else if (oStart >= start.getTime() && oStart < end.getTime()) ns = end.getTime(); // other starts inside ours: cut its head
  if (ne - ns < 60_000) return deleteSession(other);
  await saveRow('sessions', { ...other, startedAt: new Date(ns).toISOString(), endedAt: new Date(ne).toISOString(), durationSec: Math.round((ne - ns) / 1000), editedAt: new Date().toISOString() });
  await recomputeTrackedSec(other.taskId);
}

/** Move our interval so it does not intersect `others`: start after the latest earlier one, end before the earliest later one. */
export function shortenToFit(start: Date, end: Date, others: Session[]): { start: Date; end: Date } {
  let s = start.getTime(), e = end.getTime();
  for (const o of others) {
    const oS = Date.parse(o.startedAt), oE = Date.parse(o.endedAt);
    if (oS <= s && oE > s) s = oE;      // an earlier session runs into ours
    else if (oS > s && oS < e) e = oS;  // a later session starts inside ours
  }
  return { start: new Date(s), end: new Date(Math.max(e, s)) };
}

/** Work seconds per calendar day across a range, as an array aligned to `from`. */
export function dailyTotals(sessions: Session[], from: Date, days: number): number[] {
  const out = new Array<number>(days).fill(0);
  for (const s of sessions) {
    if (!isWork(s)) continue;
    const i = differenceInCalendarDays(new Date(s.startedAt), from);
    if (i >= 0 && i < days) out[i] = (out[i] ?? 0) + s.durationSec;
  }
  return out;
}

/** Split one session at an idle gap into two (`source: 'split'`), e.g. from the Track stop card (D5). */
export async function splitSession(s: Session, gapStart: Date, gapEnd: Date): Promise<[Session, Session]> {
  const a: Session = { ...s, id: uuidv7(Date.parse(s.startedAt)), endedAt: gapStart.toISOString(), durationSec: durationOf(new Date(s.startedAt), gapStart), source: 'split', editedAt: new Date().toISOString(), updatedAt: '' };
  const b: Session = { ...s, id: uuidv7(gapEnd.getTime()), startedAt: gapEnd.toISOString(), durationSec: durationOf(gapEnd, new Date(s.endedAt)), source: 'split', editedAt: new Date().toISOString(), updatedAt: '' };
  await db.transaction('rw', [db.sessions, db.outbox], async () => { await saveRow('sessions', a); await saveRow('sessions', b); await deleteRow('sessions', s); });
  await recomputeTrackedSec(s.taskId);
  return [a, b];
}

/** The longest idle stretch inside a run that is worth splitting on (≥ 5 minutes). */
export function longestIdle(segments: [string, string][], minSec = 300): { start: Date; end: Date; sec: number } | null {
  let best: { start: Date; end: Date; sec: number } | null = null;
  for (const [a, b] of segments) {
    const sec = (Date.parse(b) - Date.parse(a)) / 1000;
    if (sec >= minSec && (!best || sec > best.sec)) best = { start: new Date(a), end: new Date(b), sec };
  }
  return best;
}
